import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useBusiness } from '../../contexts/BusinessContext';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Container,
  Button,
  Paper,
  Avatar,
  Divider,
  Stack,
  Rating
} from '@mui/material';
import {
  Business as BusinessIcon,
  Star as StarIcon,
  Photo as PhotoIcon,
  Phone as PhoneIcon,
  Language as WebsiteIcon,
  LocationOn as LocationIcon,
  Schedule as ScheduleIcon,
  Category as CategoryIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { amDesignColors, amDesignTypography, amDesignComponents } from '../../themes/amDesignSystem';

const BusinessProfileDetail = () => {
  const { locationId } = useParams();
  const { selectedBusiness, selectBusinessFromUrl } = useBusiness();
  const [businessData, setBusinessData] = useState(null);
  const [reviewsData, setReviewsData] = useState(null);
  const [mediaData, setMediaData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (locationId) {
      // Auto-select business based on URL
      selectBusinessFromUrl(window.location.pathname);
      
      loadBusinessData();
    }
  }, [locationId]);

  // 🚀 UPDATED: React to business selection changes from dropdown
  useEffect(() => {
    if (selectedBusiness && locationId) {
      const businessLocationId = selectedBusiness.locationId || selectedBusiness.id;
      
      if (businessLocationId === locationId) {
        console.log('🔄 Business context updated in BusinessProfile, ensuring data is fresh:', {
          businessName: selectedBusiness.businessName,
          locationId: businessLocationId
        });
        
        // Data will be reloaded via locationId useEffect when URL changes
        // No need to manually reload here
      }
    }
  }, [selectedBusiness, locationId]);

  // Process media data when business data is loaded
  useEffect(() => {
    if (businessData) {
      loadMediaData();
    }
  }, [businessData]);

  const loadBusinessData = async () => {
    setLoading(true);
    console.log('🏢 [BUSINESS PROFILE DEBUG] Loading data for locationId:', locationId);

    try {
      // Load business data first, then reviews and media based on business data
      await loadBasicBusinessData();
      
      // Now load reviews and media in parallel
      const promises = [
        loadReviewsData(),
        // Media will be processed after business data is loaded
      ];

      await Promise.allSettled(promises);
      
    } catch (error) {
      console.error('❌ [BUSINESS PROFILE DEBUG] Error loading business data:', error);
      setError('Fehler beim Laden der Geschäftsdaten');
    } finally {
      setLoading(false);
    }
  };

  const loadBasicBusinessData = async () => {
    try {
      const response = await fetch(`/api/google-business/comprehensive/${locationId}?userId=${localStorage.getItem('user_id')}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      console.log('📊 [BUSINESS PROFILE DEBUG] Comprehensive API response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('📊 [BUSINESS PROFILE DEBUG] Business data received:', data);
        setBusinessData(data);
      } else {
        console.warn('⚠️ [BUSINESS PROFILE DEBUG] Comprehensive API failed, trying fallback');
        
        // Fallback to simple details
        const fallbackResponse = await fetch(`/api/google-business/simple-details/${locationId}?userId=${localStorage.getItem('user_id')}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        });

        if (fallbackResponse.ok) {
          const fallbackData = await fallbackResponse.json();
          console.log('📊 [BUSINESS PROFILE DEBUG] Fallback data received:', fallbackData);
          setBusinessData(fallbackData);
        }
      }
    } catch (error) {
      console.error('❌ [BUSINESS PROFILE DEBUG] Error loading basic business data:', error);
    }
  };

  const loadReviewsData = async () => {
    try {
      const response = await fetch(`/api/reviews/${locationId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      console.log('📝 [BUSINESS PROFILE DEBUG] Reviews API response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('📝 [BUSINESS PROFILE DEBUG] Reviews data received:', data);
        setReviewsData(data);
      }
    } catch (error) {
      console.error('❌ [BUSINESS PROFILE DEBUG] Error loading reviews:', error);
    }
  };

  const loadMediaData = async () => {
    try {
      // Media should be included in comprehensive API, but let's check
      if (businessData?.media_items || businessData?.media_profile) {
        setMediaData({
          media_items: businessData.media_items || [],
          media_profile: businessData.media_profile || null,
          media_cover: businessData.media_cover || null
        });
      }
    } catch (error) {
      console.error('❌ [BUSINESS PROFILE DEBUG] Error processing media:', error);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Lade Geschäftsdaten...</Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error" sx={{ mt: 3 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      {/* Header */}
      <Paper elevation={3} sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: amDesignColors.white }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={8}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {businessData?.media_profile ? (
                <Avatar
                  src={businessData.media_profile.proxyThumbnailUrl || businessData.media_profile.thumbnailUrl}
                  sx={{ width: 80, height: 80 }}
                />
              ) : (
                <Avatar sx={{ width: 80, height: 80, bgcolor: 'rgba(255,255,255,0.2)' }}>
                  <BusinessIcon sx={{ fontSize: 40 }} />
                </Avatar>
              )}
              <Box>
                <Typography variant="h4" fontWeight="bold">
                  {businessData?.title || businessData?.locationName || 'Geschäftsprofil'}
                </Typography>
                {businessData?.categories && businessData.categories.length > 0 && (
                  <Typography variant="h6" sx={{ opacity: 0.9 }}>
                    {businessData.categories[0].displayName || businessData.categories[0]}
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
              {reviewsData?.average_rating && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-start', md: 'flex-end' }, gap: 1 }}>
                  <Rating value={reviewsData.average_rating} readOnly precision={0.1} />
                  <Typography variant="h6">
                    {reviewsData.average_rating.toFixed(1)} ({reviewsData.total_reviews || 0})
                  </Typography>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={3}>
        {/* Basic Information */}
        <Grid item xs={12} md={8}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InfoIcon color="primary" />
                Grundinformationen
              </Typography>
              
              <Grid container spacing={2}>
                {businessData?.description && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Beschreibung:
                    </Typography>
                    <Typography variant="body1">
                      {businessData.description}
                    </Typography>
                  </Grid>
                )}
                
                {businessData?.address && (
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationIcon color="primary" />
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Adresse:
                        </Typography>
                        <Typography variant="body1">
                          {businessData.address.formattedAddress || 'Nicht verfügbar'}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}
                
                {businessData?.phoneNumber && (
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PhoneIcon color="primary" />
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Telefon:
                        </Typography>
                        <Typography variant="body1">
                          {businessData.phoneNumber}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}
                
                {businessData?.websiteUrl && (
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <WebsiteIcon color="primary" />
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Website:
                        </Typography>
                        <Typography variant="body1">
                          <a href={businessData.websiteUrl} target="_blank" rel="noopener noreferrer">
                            {businessData.websiteUrl}
                          </a>
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}

                {businessData?.contactCloudTenantId && (
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Cloud Tenant ID:
                        </Typography>
                        <Typography variant="body1" sx={{ fontFamily: "'JetBrains Mono', monospace", wordBreak: 'break-all' }}>
                          {businessData.contactCloudTenantId}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}

                {businessData?.categories && businessData.categories.length > 0 && (
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <CategoryIcon color="primary" />
                      <Typography variant="body2" color="text.secondary">
                        Kategorien:
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {businessData.categories.map((category, index) => (
                        <Chip 
                          key={index}
                          label={category.displayName || category}
                          variant="outlined"
                          size="small"
                        />
                      ))}
                    </Stack>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          {/* Reviews Summary */}
          {reviewsData && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <StarIcon color="primary" />
                  Bewertungen
                </Typography>
                
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <Typography variant="h3" color="primary" fontWeight="bold">
                    {reviewsData.average_rating?.toFixed(1) || '0.0'}
                  </Typography>
                  <Rating value={reviewsData.average_rating || 0} readOnly precision={0.1} />
                  <Typography variant="body2" color="text.secondary">
                    {reviewsData.total_reviews || 0} Bewertungen
                  </Typography>
                </Box>

                {reviewsData.recent_reviews && reviewsData.recent_reviews.length > 0 && (
                  <Box>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="body2" fontWeight="bold" gutterBottom>
                      Neueste Bewertung:
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      "{reviewsData.recent_reviews[0].text?.substring(0, 150)}..."
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {/* Media Summary */}
          {mediaData && mediaData.media_items && mediaData.media_items.length > 0 && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PhotoIcon color="primary" />
                  Bilder ({mediaData.media_items.length})
                </Typography>
                
                <Grid container spacing={1}>
                  {mediaData.media_items.slice(0, 6).map((image, index) => (
                    <Grid item xs={4} key={index}>
                      <img 
                        src={image.proxyThumbnailUrl || image.thumbnailUrl}
                        alt={`Geschäftsbild ${index + 1}`}
                        style={{
                          width: '100%',
                          height: '80px',
                          objectFit: 'cover',
                          borderRadius: '8px'
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
                
                {mediaData.media_items.length > 6 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                    +{mediaData.media_items.length - 6} weitere Bilder
                  </Typography>
                )}
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* Debug Information */}
      <Card sx={{ mt: 3, bgcolor: 'grey.50' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Debug-Informationen
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography variant="body2">
                <strong>Location ID:</strong> {locationId}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2">
                <strong>Business Data:</strong> {businessData ? '✅ Geladen' : '❌ Fehlt'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2">
                <strong>Reviews Data:</strong> {reviewsData ? '✅ Geladen' : '❌ Fehlt'}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Container>
  );
};

export default BusinessProfileDetail;